// ── V171: 인건비 사람별 입력 (손익 화면) ─────────────────────────────────────────
// 사장님: "인건비 입력 부분을 좀 더 상세하게 사람마다 따로 입력할 수 있게"
//   → 사람마다 기본급 · 수당·보너스 · 회사 부담 보험(社保) · 기타(숙식 등) = 합계
//   처음엔 직원 목록(인건비 탭, 월급)이나 지난달 것을 한 번에 불러오고 고친다.
// 저장: profitLoss[월].laborItems = [{ id, name, empId?, base, allowance, insurance, other, memo }]
//       그 달 인건비(costs.labor)는 사람별 합계로 같이 맞춘다 (대시보드·건강 점수·세무가 costs.labor를 본다)
import React, { useState } from 'react';
import { L, M } from '../i18n/L';

export const LABOR_PARTS = [
  { key: 'base',      label: () => L("기본급") },
  { key: 'allowance', label: () => L("수당·보너스") },
  { key: 'insurance', label: () => L("회사 부담 보험") },
  { key: 'other',     label: () => L("기타(숙식 등)") },
];
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export const laborItemTotal = (it) => r2(LABOR_PARTS.reduce((a, p) => a + (Number(it && it[p.key]) || 0), 0));
export function laborSummary(items = []) {
  const out = { people: 0, base: 0, allowance: 0, insurance: 0, other: 0, total: 0 };
  for (const it of items || []) {
    if (!it) continue;
    out.people += 1;
    for (const p of LABOR_PARTS) out[p.key] = r2(out[p.key] + (Number(it[p.key]) || 0));
  }
  out.total = r2(out.base + out.allowance + out.insurance + out.other);
  return out;
}
const newId = () => 'lb' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
/** 지난달 → 이번 달 (같은 사람·금액, 메모는 비움) */
export const copyLaborItems = (items = []) => (items || []).filter(Boolean).map(it => ({ ...it, id: newId(), memo: '' }));
/** 직원 목록 → 사람별 줄 (쓰는 직원만, 월급이면 기본급) */
export const laborItemsFromEmployees = (employees = []) => (employees || [])
  .filter(e => e && e.active !== false && String(e.name || '').trim())
  .map(e => ({ id: newId(), empId: e.id, name: String(e.name).trim(), base: Number(e.monthlyWage) || 0, allowance: 0, insurance: 0, other: 0, memo: '' }));

const numIn = { width: '100%', minWidth: 0, padding: '7px 8px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, textAlign: 'right', background: '#fff', boxSizing: 'border-box' };
const show3 = (v) => (v ? String(Math.round(Number(v) * 1000) / 1000) : '');

export default function LaborDetail({ items = [], prevItems = [], employees = [], onChange, isMobile, money = M }) {
  const [open, setOpen] = useState(items.length > 0);
  const sum = laborSummary(items);
  const set = (id, patch) => onChange(items.map(it => (it.id === id ? { ...it, ...patch } : it)));
  const add = () => { onChange([...items, { id: newId(), name: '', base: 0, allowance: 0, insurance: 0, other: 0, memo: '' }]); setOpen(true); };
  const fromEmp = laborItemsFromEmployees(employees);

  return (
    <div data-pl-labor style={{ margin: '-4px 0 12px 0' }}>
      <button type="button" data-pl-labor-toggle onClick={() => setOpen(o => !o)}
        style={{ border: 'none', background: 'none', color: '#1A56A0', fontSize: 12, fontWeight: 800, cursor: 'pointer', padding: '2px 0' }}>
        👥 {items.length ? L("사람별 인건비 {0}명 · {1}", sum.people, money(sum.total)) : L("사람별로 입력하기")} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={{ marginTop: 6, border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#FAFAFA' }}>
          {items.length === 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {prevItems.length > 0 && (
                <button type="button" data-pl-labor-copy onClick={() => onChange(copyLaborItems(prevItems))} style={btn(true)}>
                  📋 {L("지난달 {0}명 그대로 가져오기", prevItems.length)}
                </button>
              )}
              {fromEmp.length > 0 && (
                <button type="button" data-pl-labor-emp onClick={() => onChange(fromEmp)} style={btn(prevItems.length === 0)}>
                  👥 {L("직원 목록에서 {0}명 불러오기", fromEmp.length)}
                </button>
              )}
            </div>
          )}
          {!isMobile && items.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 6, fontSize: 11, color: '#6b7280', fontWeight: 700, padding: '0 2px 4px' }}>
              <span>{L("이름")}</span>{LABOR_PARTS.map(p => <span key={p.key} style={{ textAlign: 'right' }}>{p.label()}</span>)}<span style={{ textAlign: 'right' }}>{L("합계")}</span><span />
            </div>
          )}
          {items.map(it => (
            <div key={it.id} data-pl-labor-item={it.name || it.id}
              style={isMobile
                ? { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 9, padding: 8, marginBottom: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }
                : { display: 'grid', gridTemplateColumns: GRID, gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <input data-pl-labor-name value={it.name || ''} onChange={e => set(it.id, { name: e.target.value })} placeholder={L("이름")}
                style={{ ...numIn, textAlign: 'left', ...(isMobile ? { gridColumn: '1 / -1' } : {}) }} />
              {LABOR_PARTS.map(p => (
                <label key={p.key} style={{ display: 'block', minWidth: 0 }}>
                  {isMobile && <span style={{ fontSize: 10, color: '#6b7280' }}>{p.label()}</span>}
                  <input type="number" min="0" data-pl-labor-part={p.key} value={show3(it[p.key])} placeholder="0"
                    onChange={e => set(it.id, { [p.key]: Number(e.target.value) || 0 })} style={numIn} />
                </label>
              ))}
              <span data-pl-labor-total style={{ fontSize: 13, fontWeight: 800, textAlign: 'right', ...(isMobile ? { alignSelf: 'end' } : {}) }}>{money(laborItemTotal(it))}</span>
              <button type="button" onClick={() => { if (window.confirm(L("{0} 줄을 지울까요?", it.name || L("이")))) onChange(items.filter(x => x.id !== it.id)); }}
                style={{ border: 'none', background: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 13, justifySelf: isMobile ? 'end' : 'center' }}>🗑</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
            <button type="button" data-pl-labor-add onClick={add} style={btn(false)}>＋ {L("사람 추가")}</button>
            {items.length > 0 && (
              <button type="button" data-pl-labor-clear onClick={() => { if (window.confirm(L("사람별 입력을 지우고 인건비를 한 칸으로 입력할까요?"))) onChange([]); }}
                style={{ border: 'none', background: 'none', color: '#9ca3af', fontSize: 11, cursor: 'pointer' }}>{L("사람별 입력 지우기")}</button>
            )}
          </div>
          {items.length > 0 && (
            <div data-pl-labor-sum style={{ marginTop: 8, fontSize: 12, color: '#3f3f46', lineHeight: 1.6 }}>
              <b>{L("합계 {0} · {1}명", money(sum.total), sum.people)}</b>
              <span style={{ color: '#6b7280' }}> — {LABOR_PARTS.map(p => `${p.label()} ${money(sum[p.key])}`).join(' · ')}</span>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>{L("이 합계가 이달 인건비로 들어가요. 회사 부담 보험은 직원 월급에서 떼는 몫이 아니라 사장님이 따로 내는 몫(社保 회사 부담)이에요.")}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const GRID = 'minmax(90px, 1.3fr) repeat(4, minmax(70px, 1fr)) minmax(70px, 0.9fr) 28px';
const btn = (primary) => ({
  padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer',
  border: primary ? 'none' : '1.5px dashed #FF6F0F', background: primary ? '#FF6F0F' : '#fff', color: primary ? '#fff' : '#C2410C',
});
